import ErrorBoundary from "@/components/common/ErrorBoundary";
import {
  useCourses,
  useCourseStatus,
  useCourseTopPerformers,
  useDepartmentComparison,
  useMonthlyChartData,
} from "@/hooks/queries/useCourseQueries";
import { useAuth } from "@/hooks/useAuth";
import React, { useMemo, useState } from "react";
import {
  HeaderSection,
  LoadingState,
  MetricsCards,
  ChartsSection,
  TablesSection,
} from "@/components/analytics";

const Analytics = () => {
  const [selectedCourse, setSelectedCourse] = useState<string>("");
  const { data: courses = [] } = useCourses();
  const { data: courseStatus, isLoading: courseStatusLoading } =
    useCourseStatus(selectedCourse);
  const { data: courseTopPerformers, isLoading: courseTopPerformersLoading } =
    useCourseTopPerformers(selectedCourse);
  const { user } = useAuth();
  const { data: monthlyData, isLoading, isError } = useMonthlyChartData();

  // Get recent courses (last 3 courses)
  const recentCourses = courses.slice(0, 3).map(course => ({
    title: typeof course.title === "string" 
      ? course.title 
      : course.title?.en || course.title?.am || "Untitled Course",
    enrollments: (course as any).enrollments || 0,
    completion: (course as any).completionRate || 0,
    rating: (course as any).rating || 0,
    createdAt: (course as any).createdAt || (course as any).updatedAt,
  }));

  // Only fetch department comparison if user is loaded and is a company admin
  const shouldFetchDepartmentComparison = user?.role === "company_admin";
  const { data: departments = [], isLoading: departmentsLoading, error: departmentsError } =
    useDepartmentComparison(shouldFetchDepartmentComparison, user?.role);

  const departmentChartData = useMemo(() => {
    if (!departments || !Array.isArray(departments) || departments.length === 0) {
      return null;
    }

    const labels = departments.map((d) => d.departmentName || 'Unknown Department');
    const data = departments.map((d) => {
      // Display the actual data from the backend
      // If totalScore is already a percentage, use it directly
      // If it's a raw number, we'll display it as is
      return d.totalScore || 0;
    });

    return {
      labels,
      datasets: [
        {
          label: "Department Score",
          data,
          backgroundColor: [
            "rgba(0, 95, 86, 0.8)",
            "rgba(59, 130, 246, 0.8)",
            "rgba(249, 115, 22, 0.8)",
            "rgba(34, 197, 94, 0.8)",
            "rgba(245, 158, 11, 0.8)",
            "rgba(239, 68, 68, 0.8)",
            "rgba(168, 85, 247, 0.8)",
            "rgba(236, 72, 153, 0.8)",
          ],
          borderColor: [
            "rgba(0, 95, 86, 1)",
            "rgba(59, 130, 246, 1)",
            "rgba(249, 115, 22, 1)",
            "rgba(34, 197, 94, 1)",
            "rgba(245, 158, 11, 1)",
            "rgba(239, 68, 68, 1)",
            "rgba(168, 85, 247, 1)",
            "rgba(236, 72, 153, 1)",
          ],
          borderWidth: 2,
          borderRadius: 8,
          borderSkipped: false,
        },
      ],
    };
  }, [departments]);

  // Set default selected course to the first one when courses are loaded
  React.useEffect(() => {
    if (courses.length > 0 && !selectedCourse) {
      setSelectedCourse(courses[0]._id);
    }
  }, [courses, selectedCourse]);

  const handleCourseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCourse(e.target.value);
  };

  const completionRate = courseStatus && courses.length > 0
    ? Math.round((courseStatus.completed / courses.length) * 100)
    : 0;

  if (courses.length === 0) {
    return <LoadingState />;
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <HeaderSection
          courses={courses}
          selectedCourse={selectedCourse}
          onCourseChange={handleCourseChange}
        />

        <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-8">
          <MetricsCards
            courses={courses}
            courseStatus={courseStatus}
            completionRate={completionRate}
          />

          <ChartsSection
            selectedCourse={selectedCourse}
            courseStatus={courseStatus}
            courseStatusLoading={courseStatusLoading}
            monthlyData={monthlyData}
            isLoading={isLoading}
            isError={isError}
            user={user}
            departments={departments}
            departmentsLoading={departmentsLoading}
            departmentsError={departmentsError}
            departmentChartData={departmentChartData}
          />

          <TablesSection
            selectedCourse={selectedCourse}
            courseTopPerformers={courseTopPerformers}
            courseTopPerformersLoading={courseTopPerformersLoading}
            recentCourses={recentCourses}
          />
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default Analytics;